import {
  BehaviorSubject,
  NEVER,
  Observable,
  defer,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  of,
  pairwise,
  startWith,
  switchMap,
  take,
  takeUntil,
  tap,
  timer,
} from 'rxjs';

export interface BusyTiming {
  /** Wait this long before showing the indicator (hides very fast requests). */
  delayMs: number;
  /** Once shown, keep it at least this long (avoids a flash). */
  minVisibleMs: number;
}

/** Timings per kind of indicator (UX spec "feedback de carga"). */
export const BUSY_TIMING = {
  /** Spinner inside an input, button or loose control. */
  control: { delayMs: 200, minVisibleMs: 300 },
  /** Progress bar above a table. */
  table: { delayMs: 150, minVisibleMs: 300 },
  /** Full-screen blocking overlay. */
  overlay: { delayMs: 150, minVisibleMs: 400 },
} as const satisfies Record<string, BusyTiming>;

/**
 * Turns a raw "is busy" stream into a "show the indicator" stream applying the
 * delay and the minimum visible time, so screens do not re-implement them.
 * Disable the control immediately from the raw stream; use this one only for
 * the icon.
 */
export function showAfterDelay(
  busy$: Observable<boolean>,
  timing: BusyTiming = BUSY_TIMING.control,
): Observable<boolean> {
  return defer(() => {
    let shownAt: number | null = null;

    return busy$.pipe(
      distinctUntilChanged(),
      switchMap((busy) => {
        if (busy) {
          if (shownAt !== null) {
            return of(true);
          }
          return timer(timing.delayMs).pipe(
            tap(() => (shownAt = Date.now())),
            map(() => true),
          );
        }

        if (shownAt === null) {
          return of(false);
        }

        const remaining = Math.max(
          0,
          timing.minVisibleMs - (Date.now() - shownAt),
        );
        return timer(remaining).pipe(
          tap(() => (shownAt = null)),
          map(() => false),
        );
      }),
      startWith(false),
      distinctUntilChanged(),
    );
  });
}

/**
 * Counts in-flight requests so the busy flag stays `true` while at least one
 * is pending. With `switchMap` (typeahead) the previous request is cancelled
 * and the next one starts in the same tick: going idle is deferred one macro
 * task so the flag (and the indicator delay) does not restart on every key.
 */
export class BusyTracker {
  private inFlight = 0;
  private readonly state$ = new BehaviorSubject<boolean>(false);

  readonly busy$: Observable<boolean> = this.state$.pipe(
    switchMap((busy) => (busy ? of(true) : timer(0).pipe(map(() => false)))),
    startWith(false),
    distinctUntilChanged(),
  );

  /** Wraps a source: counts it as busy from subscription until it ends or is unsubscribed. */
  track<T>(source$: Observable<T>): Observable<T> {
    return defer(() => {
      this.inFlight += 1;
      this.state$.next(true);
      return source$.pipe(
        finalize(() => {
          this.inFlight = Math.max(this.inFlight - 1, 0);
          if (this.inFlight === 0) {
            this.state$.next(false);
          }
        }),
      );
    });
  }

  /** Indicator stream with the delay and minimum visible time applied. */
  indicator$(timing: BusyTiming = BUSY_TIMING.control): Observable<boolean> {
    return showAfterDelay(this.busy$, timing);
  }
}

/**
 * First result of a fire-and-forget list request (`service.search(q)` +
 * `service.getX()`), completing also when the service's loading flag goes
 * from busy to idle without a result. List services toast the error and only
 * clear the flag, so without this a failed search would keep a tracked
 * spinner on forever.
 */
export function firstResultUntilIdle<T>(
  result$: Observable<T>,
  loading$: Observable<boolean> | undefined,
): Observable<T> {
  const wentIdle$ = (loading$ ?? NEVER).pipe(
    pairwise(),
    filter(([wasLoading, loading]) => wasLoading && !loading),
  );

  return result$.pipe(take(1), takeUntil(wentIdle$));
}
